import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PortraitIcon from '@mui/icons-material/Portrait';
import RadarIcon from '@mui/icons-material/Radar';
import TodayIcon from '@mui/icons-material/Today';
import WarningIcon from '@mui/icons-material/Warning';
import WorkspacesIcon from '@mui/icons-material/Workspaces';

import { selectAttendedStatusId } from '../../../../store/dataSelectors';
import { selectUsersForNames } from '../../../../store/userSelectors';
import { reverseFormatDate } from '../../../../utils/dateFunctions';
import CheckBox from '../../../formElements/CheckBox';
import SingleSelect from '../../../formElements/SingleSelect';

import card from '../../../../commonStyles/card.module.css';
import form from '../../../formElements/formElementStyles/checkBox.module.css';

const _SELECTED_MESSAGE = 'Module selected to attend';

const ParticipantAttendanceStatusView = ({
    module,
    currentParticipant,
    information,
    attendanceDetails,
    onModuleChange,
    isStatusUpdateDisabled = false,
    isModuleCheckDisabled = false
}) => {
    // LOCAL STATE
    const {
        title,
        description,
        locationId,
        date,
        startTime,
        endTime,
        userId,
        capacity,
        smartTarget,
        participants
    } = module;

    const [courseLocation, setCourseLocation] = useState('');
    const [adviser, setAdviser] = useState('');
    const [statusName, setStatusName] = useState('');
    const [statusId, setStatusId] = useState('');
    const [newModule, setNewModule] = useState({});
    const [isTimePast, setIsTimePast] = useState(false);

    // STORE STATE
    const users = useSelector(selectUsersForNames);
    const locations = useSelector((state) => state.entities.directusService.locationIdsAndNames);
    const attendedStatusId = useSelector(selectAttendedStatusId);

    // USEEFFECTS
    useEffect(() => {
        setIsTimePast(date + 'T' + endTime <= format(new Date(), "yyyy-MM-dd'T'HH:mm"));
        let updatedModule = { ...module };
        let newStatus;
        newStatus = attendanceDetails.find(
            (entry) =>
                entry.id ===
                (participants.find((el) => el.participantId === currentParticipant.id)?.statusId ||
                    '')
        );
        if (newStatus?.name?.toLowerCase() === 'not known') {
            const updatedParticipants = updateParticipants(true);
            updatedModule = {
                ...module,
                checked: true,
                participants: updatedParticipants
            };
        }
        setNewModule(updatedModule);
        if (!newStatus)
            newStatus = attendanceDetails.find((el) => el.name.toLowerCase() === 'not known');
        setStatusId(newStatus.id);
        setStatusName(newStatus.name.toLowerCase());
    }, []);

    useEffect(() => {
        if (users.length) {
            const user = users.find((el) => el.id === userId);
            user && addAdviser(user);
        }
    }, [users]);

    useEffect(() => {
        if (locations?.length > 0) {
            const location = locations.find((el) => el.id === locationId);
            location && setCourseLocation(location.name);
        }
    }, [locations]);

    // HELPER FNS
    const addAdviser = (adviser) => {
        const { firstName, lastName } = adviser;
        setAdviser(`${firstName} ${lastName}`);
    };

    const isParticipantSelected = () =>
        participants.find((el) => el.participantId === currentParticipant.id);

    const updateParticipants = (checked) => {
        if (checked) {
            return !participants.some((el) => el.participantId === currentParticipant.id)
                ? [
                      ...participants,
                      {
                          participantId: currentParticipant.id,
                          statusId,
                          status: statusName,
                          notes: information
                      }
                  ]
                : participants;
        } else {
            return !isTimePast
                ? participants.filter((el) => el.participantId !== currentParticipant.id)
                : participants;
        }
    };

    // EVENT HANDLERS
    const onCheck = (e) => {
        const { checked } = e.target;
        const updatedParticipants = updateParticipants(checked);
        const updatedModule = {
            ...module,
            checked,
            participants: updatedParticipants
        };
        setNewModule(updatedModule);
        onModuleChange(updatedModule, null);
    };

    const onStatusIdChange = (chosenId) => {
        if (chosenId !== '') {
            setStatusId(chosenId);
            setStatusName(
                attendanceDetails.find((el) => el.id === chosenId)?.name.toLowerCase() || ''
            );
            const updatedParticipants = participants.map((el) =>
                el.participantId === currentParticipant.id ? { ...el, statusId: chosenId } : el
            );
            const updatedModule = { ...newModule, participants: updatedParticipants };
            setNewModule(updatedModule);
            let actionPlanData = null;
            if (chosenId === attendedStatusId && module.smartTarget) {
                actionPlanData = {
                    participantId: currentParticipant.id,
                    mandatory: '',
                    actionTitle: smartTarget.name,
                    action: smartTarget.action,
                    actionStartDate: '',
                    actionDueDate: '',
                    actionOutcomeDate: '',
                    outcomeId: '',
                    moduleId: module.id,
                    comments: ''
                };
            }
            onModuleChange(updatedModule, actionPlanData);
        }
    };

    // RENDER
    return (
        <div
            className={`${card.card} ${card.attendanceStatusCard} ${isTimePast && !isParticipantSelected() ? card.greyCard : card.blueCard} `}
            data-testid="module_status_card_data">
            <div className={`${title?.length > 60 ? card.bannerDoubleRow : card.banner}`}>
                <h6>{title || 'Participant Status'}</h6>
            </div>

            <div className={card.content}>
                <div>
                    <div className={card.subHeader}>Module Description: </div>
                    <div className={card.description}>
                        {description && description.length > 100
                            ? description.substring(0, 97) + '...'
                            : description}
                    </div>
                </div>
                <div className={`${card.rows} ${card.compactRows}`}>
                    <div className={card.row}>
                        <LocationOnIcon />
                        {` ${courseLocation ? courseLocation : ' - '}`}
                    </div>
                    <div className={card.row}>
                        <TodayIcon />
                        {!date && !startTime && !endTime
                            ? ' - '
                            : ` ${date ? reverseFormatDate(date) : ''} ${
                                  startTime ? startTime : ''
                              } ${endTime ? endTime : ''}`}
                    </div>
                    <div className={card.row}>
                        <PortraitIcon />
                        {adviser || ' - '}
                    </div>
                    <div className={card.row}>
                        <RadarIcon />
                        <span className={card.smartTargetName}>{smartTarget?.name || ' - '}</span>
                    </div>
                </div>

                {!isTimePast && (
                    <div className={card.checkRow}>
                        <CheckBox
                            id="submitParticipant"
                            key={module.id}
                            disabled={isModuleCheckDisabled}
                            checked={newModule.checked || false}
                            onChange={onCheck}
                        />
                        {_SELECTED_MESSAGE}
                    </div>
                )}

                <div className={card.actions}>
                    {isTimePast && (
                        <div className={card.attendanceIcons}>
                            {!isParticipantSelected() ? (
                                <div className={card.checkRow}>
                                    <span className={form.emptyCheckBox} />
                                    {_SELECTED_MESSAGE}
                                </div>
                            ) : statusName === 'not known' && isParticipantSelected() ? (
                                <SingleSelect
                                    id={'statusId'}
                                    placeholder="Select status..."
                                    disabled={isStatusUpdateDisabled}
                                    customClass="restrictHeightAbsolute"
                                    menuItems={attendanceDetails || []}
                                    selectedId={statusId}
                                    selected={attendanceDetails.find((el) => el.id === statusId)}
                                    onChange={(chosenId) => onStatusIdChange(chosenId)}
                                />
                            ) : statusName === 'attended' ? (
                                <span>
                                    <CheckCircleIcon className={`${card.icon} ${card.green}`} />{' '}
                                    Attended Module
                                </span>
                            ) : statusName === 'failed to attend' ? (
                                <span>
                                    <CancelIcon className={`${card.icon} ${card.red}`} /> Failed To
                                    Attend
                                </span>
                            ) : statusName === 'cancelled - adviser' ? (
                                <span>
                                    <CancelIcon className={`${card.icon} ${card.red}`} /> Adviser
                                    Cancelled
                                </span>
                            ) : statusName === 'cancelled - participant' ? (
                                <span>
                                    <CancelIcon className={`${card.icon} ${card.red}`} />{' '}
                                    Participant Cancelled
                                </span>
                            ) : (
                                <span>
                                    <WarningIcon className={`${card.icon} ${card.yellow}`} /> Status
                                    Not Set
                                </span>
                            )}
                        </div>
                    )}

                    <div className={card.attendanceView}>
                        <span className={card.spaces}>
                            <WorkspacesIcon />
                            {participants &&
                                ` ${participants.length} of ${capacity ? capacity : ''} spaces filled`}
                            {!participants && ` 0 spaces filled`}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

ParticipantAttendanceStatusView.propTypes = {
    module: PropTypes.object.isRequired,
    attendanceDetails: PropTypes.array,
    currentParticipant: PropTypes.object,
    information: PropTypes.string,
    onModuleChange: PropTypes.func,
    isModuleCheckDisabled: PropTypes.bool,
    isStatusUpdateDisabled: PropTypes.bool
};

export default ParticipantAttendanceStatusView;
